import React, { useState } from "react";
import { EmailStatus, IEmail, } from "../models";
import moment from "moment";
import Body from "modules/events/components/event-views/components/body";
import { useSelector } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { PersonalizationTokenInformation, replaceTextPersonalizationTokens } from "utils/replaceTextPersonalizationTokens";
import { AttachedFile } from "modules/gallery";
import { Grid, Typography } from "@mui/material";
import { documentsApi } from "api/instances";
import { FileDownloader } from "utils/fileDownloader";
import BaseComponentOverlay from "modules/common/components/overlays/baseComponentOverlay";
import Loading from "modules/common/components/loading";

import "../styles/emailContentView.sass";


interface IEmailContentViewProps {
    email: IEmail;
    activeLcid: string;
    tenantName: string;
}

const EmailContentView: React.FC<IEmailContentViewProps> = ({
    email,
    activeLcid,
    tenantName
}) => {
    const currentUser = useSelector((state: GlobalApplicationState) => state.settings.currentUser);
    const isDraft = email.status === EmailStatus.Draft;
    const [isLoading, setIsLoading] = useState(false);

    const getFormattedSentTime = () => {
        let sentTime = moment().calendar();
        if (
            email.sentTime && // sent time must be available
            (
                !isDraft || // if email is scheduled/sent, we use the sent time attached to the email
                (isDraft && new Date(email.sentTime) >= new Date()) // if email is draft, we use the sent time only if it's in the future
            )
        ) {
            sentTime = moment(email.sentTime).format("ddd, MMM D, YYYY [at] h:mm A");
        }
        return sentTime;
    }

    const onClickEmailAttachment = async (attachment: AttachedFile) => {
        try {
            setIsLoading(true);
            const file = {
                name: `${attachment.fileName}`
            };
            const fileData = await documentsApi.GetFile(attachment.ospreyId);
            new FileDownloader(file).downloadDoc(fileData);
        }
        catch { }
        finally {
            setIsLoading(false);
        }
    }

    const getAttachmentItem = (attachment: AttachedFile) => 
        <Grid key={attachment.ospreyId} item xs={4} zeroMinWidth>
            <div className="email-attachment-item" key={attachment.ospreyId} onClick={() => onClickEmailAttachment(attachment)}>
                <img className="email-attachment-item-icon" src={`/images/icons/documents/${attachment.fileExtension}.png`} alt={attachment.fileExtension} />
                <Typography className="email-attachment-item-name">
                    {attachment.fileName}
                </Typography>
            </div>
        </Grid>;

    const fetchPersonalizationTokenDetails = (): PersonalizationTokenInformation => {
        return {
            currentUser,
            tenantName
        };
    }

    return <div className="email-content-view">
        <div style={{ backgroundColor: "#ffffff" }}>
            <div className="email-page-content">
                <div className="subject">
                    {
                        email.subject && email.subject.find(s => s.lcid == activeLcid)?.text 
                            ? replaceTextPersonalizationTokens(email.subject, activeLcid, fetchPersonalizationTokenDetails()) 
                            : "No subject"
                    }
                </div>
                <div className="email-header-container">
                    <div className="sender">
                        <img className="sender-image" alt="Placeholder image for a user" src="/images/email-user-placeholder.png"/>
                        <div className="sender-info-container">
                            <div className="sender-name">
                                <b>{email.fromSender.name}</b>
                                <span className="info-span">{` <${email.fromSender.email}>`}</span>
                            </div>
                            <span className="info-span">to me</span>
                        </div>
                    </div>
                    <div className="email-action-buttons">
                        <div className="mock-buttons">
                            <img className="reply-icon" alt="Reply icon" src="/images/icons/reply_icon.svg"/>
                            <img className="reply-all-icon" alt="Reply all icon" src="/images/icons/reply_all_icon.svg"/>
                            <img className="forward-icon" alt="Forward icon" src="/images/icons/reply_icon.svg"/>
                        </div>
                        <div className="info-span">{getFormattedSentTime()}</div>
                    </div>
                </div>
                {
                    email.fileAttachments.length > 0 &&
                    <div className={`email-attachments-container ${email.fileAttachments.length > 3 ? "email-attachments-container-scrollable" : ""}`}>                    
                        <Grid className="email-attachments-grid" container spacing={1}>
                            {
                                email.fileAttachments.map(getAttachmentItem)
                            }
                        </Grid>
                    </div>
                }
                <Body body={replaceTextPersonalizationTokens(email.body, activeLcid, fetchPersonalizationTokenDetails())}/>
            </div>
        </div>
        <BaseComponentOverlay 
            show={isLoading} 
            middleComponent={<Loading />} 
            fullScreen
            backdropSxProps={{backgroundColor: 'rgba(255, 255, 255, 0.5)'}}
        />
    </div>;
};

export default EmailContentView;
